import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Calendar } from 'lucide-react';
import { COMPANY, BRAND_LOGO, BOOKING_URL } from '../mock';

const links = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/prices', label: 'Prices' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
  { to: '/fly-tipping', label: 'Fly-tipping' },
];

const PINNED = links.slice(-2);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [setScrolled]);

  useEffect(() => { setOpen(false); }, [location.pathname, setOpen]);

  return (
    <header className={`sticky top-0 z-50 bg-white transition-shadow ${scrolled ? 'shadow-[0_6px_20px_-10px_rgba(11,37,69,0.25)]' : ''}`}>
      <div className="container-xl flex items-center justify-between py-4">
        <Link to="/" className="flex items-center gap-2.5 group">
          <img src={BRAND_LOGO} alt="Move Van Ltd" className="h-14 md:h-16 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-8 mx-4">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              className={({ isActive }) => `nav-link whitespace-nowrap ${isActive ? 'text-[color:var(--mv-orange)]' : ''}`}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-[color:var(--mv-navy)] font-semibold whitespace-nowrap" aria-label={`Call ${COMPANY.phone}`}>
            <span className="pulse-dot" />
            <Phone className="w-4 h-4" />
            <span className="hidden xl:inline">{COMPANY.phone}</span>
          </a>
          <a href="/#quote" className="btn-primary whitespace-nowrap" data-testid="nav-get-quote">
            <Calendar className="w-4 h-4" /> Get a quote
          </a>
        </div>

        <button className="lg:hidden p-2 text-[color:var(--mv-navy)]" onClick={() => setOpen(!open)} aria-label="Toggle menu" aria-expanded={open} aria-controls="mobile-menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <nav aria-label="Contact and fly-tipping" className="lg:hidden border-t border-slate-100 bg-white" data-testid="nav-mobile-pinned">
        <div className="container-xl grid grid-cols-2 gap-2 py-1.5">
          {PINNED.map((l) => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => `h-11 rounded-md inline-flex items-center justify-center text-sm font-bold border ${isActive ? 'bg-orange-50 border-[color:var(--mv-orange)] text-[color:var(--mv-orange)]' : 'border-slate-200 text-[color:var(--mv-navy)]'}`} data-testid={`nav-pinned-${l.label.toLowerCase()}`}>
              {l.label}
            </NavLink>
          ))}
        </div>
      </nav>

      {open && (
        <div id="mobile-menu" className="lg:hidden border-t border-slate-100 bg-white">
          <div className="container-xl py-4 flex flex-col gap-1">
            {links.filter((l) => !PINNED.includes(l)).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                className={({ isActive }) => `py-3 px-2 rounded-md font-semibold ${isActive ? 'text-[color:var(--mv-orange)] bg-orange-50' : 'text-[color:var(--mv-navy)] hover:bg-slate-50'}`}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="mt-2 btn-ghost-dark justify-center">
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
            <a href="https://wa.me/447985291097" target="_blank" rel="noreferrer" className="btn-ghost-dark justify-center" data-testid="nav-mobile-whatsapp">
              WhatsApp us
            </a>
            <a href="/#quote" onClick={() => setOpen(false)} className="btn-primary justify-center" data-testid="nav-mobile-quote">
              <Calendar className="w-4 h-4" /> Get a quote
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
